import * as React from "react"
import { cn } from "../../lib/utils"

const Card = React.forwardRef(
  ({ className, variant = "default", animation = "none", hover = "none", ...props }, ref) => {
    const variantClasses = {
      default: "bg-card border-border dark:bg-card dark:border-border",
      glass: "glass border-transparent",
      gradient: "bg-gradient-card dark:bg-gradient-card-dark backdrop-blur-sm border-white border-opacity-30 dark:border-gray-700 dark:border-opacity-30",
      neumorphic: "neumorphic border-transparent",
      "neumorphic-inset": "neumorphic-inset border-transparent",
      accent: "border-primary border-opacity-20 bg-primary bg-opacity-5 dark:border-primary-light dark:border-opacity-20 dark:bg-primary-dark dark:bg-opacity-10",
      subtle: "bg-card border-border bg-subtle-texture dark:bg-card dark:border-border dark:bg-subtle-texture",
    };
    
    const animationClasses = {
      none: "",
      "fade-in": "animate-fade-in",
      "slide-up": "animate-slide-up",
      "scale-in": "animate-scale-in",
      "float": "animate-float",
    };
    
    const hoverClasses = {
      none: "",
      "lift": "hover:translate-y-[-5px] hover:shadow-md transition-all",
      "glow": "hover:shadow-[0_0_15px_rgba(var(--color-primary-rgb),0.5)] transition-all",
      "scale": "hover:scale-[1.02] transition-all",
      "border": "hover:border-primary hover:border-opacity-50 dark:hover:border-primary-light dark:hover:border-opacity-50 transition-all",
    };
    
    return (
      <div
        ref={ref}
        className={cn(
          "rounded-xl border shadow-sm transition-all duration-300",
          variantClasses[variant] || variantClasses.default,
          animationClasses[animation],
          hoverClasses[hover],
          className
        )}
        {...props}
      />
    )
  }
)
Card.displayName = "Card"

const CardHeader = React.forwardRef(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex flex-col space-y-1.5 p-6", className)}
      {...props}
    />
  )
)
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef(
  ({ className, children, gradient = false, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn(
        "text-2xl font-semibold leading-none tracking-tight",
        gradient ? "text-gradient-primary" : "text-text dark:text-text",
        className
      )}
      {...props}
    >
      {children}
    </h3>
  )
)
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={cn("text-sm text-text-light dark:text-text-light", className)}
      {...props}
    />
  )
)
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
  )
)
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex items-center p-6 pt-0", className)}
      {...props}
    />
  )
)
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
